import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { StoreLayout } from './components/layout/StoreLayout';
import { Spinner } from './components/ui';
import { useAuth } from './hooks/useAuth';

const HomePage = lazy(() => import('./pages/HomePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const CollectionsIndexPage = lazy(() => import('./pages/CollectionsIndexPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const TagPage = lazy(() => import('./pages/TagPage'));
const NewArrivalsPage = lazy(() => import('./pages/NewArrivalsPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const CustomOrderPage = lazy(() => import('./pages/CustomOrderPage'));
const ContentPage = lazy(() => import('./pages/ContentPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const AccountLayout = lazy(() => import('./pages/account/AccountLayout'));
const AccountDashboard = lazy(() => import('./pages/account/AccountDashboard'));
const AccountOrders = lazy(() => import('./pages/account/AccountOrders'));
const AccountOrderDetail = lazy(() => import('./pages/account/AccountOrderDetail'));
const AccountAddresses = lazy(() => import('./pages/account/AccountAddresses'));
const AccountWishlist = lazy(() => import('./pages/account/AccountWishlist'));
const AccountProfile = lazy(() => import('./pages/account/AccountProfile'));
const AccountSupport = lazy(() => import('./pages/account/AccountSupport'));
const AccountSupportNew = lazy(() => import('./pages/account/AccountSupportNew'));
const AccountSupportTicket = lazy(() => import('./pages/account/AccountSupportTicket'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const AdminApp = lazy(() => import('./admin/AdminApp'));

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: window.location.pathname }} />;
  return children;
}

export function App() {
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route element={<StoreLayout />}>
          <Route index element={<HomePage />} />
          <Route path="shop" element={<ShopPage />} />
          <Route path="c/:slug" element={<CategoryPage />} />
          <Route path="collections" element={<CollectionsIndexPage />} />
          <Route path="collections/:slug" element={<CollectionPage />} />
          <Route path="new-arrivals" element={<NewArrivalsPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="p/:slug" element={<ProductPage />} />
          <Route path="tag/:slug" element={<TagPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="order/:id" element={<OrderSuccessPage />} />
          <Route path="custom-orders" element={<CustomOrderPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="unsubscribe" element={<UnsubscribePage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="pages/:slug" element={<ContentPage />} />
          <Route path="about" element={<Navigate to="/pages/about" replace />} />

          <Route path="login" element={<LoginPage />} />
          {/* customers sign in with their phone number only; old /register links land on the login page */}
          <Route path="register" element={<Navigate to="/login" replace />} />

          <Route
            path="account"
            element={
              <RequireAuth>
                <AccountLayout />
              </RequireAuth>
            }
          >
            <Route index element={<AccountDashboard />} />
            <Route path="orders" element={<AccountOrders />} />
            <Route path="orders/:id" element={<AccountOrderDetail />} />
            <Route path="addresses" element={<AccountAddresses />} />
            <Route path="wishlist" element={<AccountWishlist />} />
            <Route path="profile" element={<AccountProfile />} />
            <Route path="support" element={<AccountSupport />} />
            <Route path="support/new" element={<AccountSupportNew />} />
            <Route path="support/:ref" element={<AccountSupportTicket />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route path="/admin/*" element={<AdminApp />} />
      </Routes>
    </Suspense>
  );
}
